import {
  Banknote,
  Briefcase,
  CalendarRange,
  ClipboardList,
  Cpu,
  Headset,
  LucideIcon,
  Palette,
  Printer,
  ShoppingCart,
} from "lucide-react";
import { PermissaoEnum } from "@workflow/types";

import { ROUTES } from "@/lib/navigation-helpers";

/**
 * Uma visão é a mesma entidade vista por uma área (a planilha é o trabalho de
 * todos, cada área tem sua rota). Fonte única para o guarda de rota decidir,
 * quando o link recebido não é da área da pessoa, por onde ela pode entrar.
 */
export type ResourceView = {
  key: string;
  label: string;
  /** O que a pessoa encontra nessa visão — texto do card de escolha. */
  descricao: string;
  icon: LucideIcon;
  permissao: PermissaoEnum;
  path: (id: string | number) => string;
};

export const RESOURCE_VIEWS = {
  planilha: [
    {
      key: "atendimento",
      label: "Atendimento",
      descricao: "Cliente, prazos e faturamento da planilha",
      icon: Headset,
      permissao: PermissaoEnum.PLANILHA_ATENDIMENTO_FIND_ALL,
      path: ROUTES.atendimentoDetails,
    },
    {
      key: "pre-producao",
      label: "Pré-produção",
      descricao: "Versões, itens e aprovação do orçamento",
      icon: ClipboardList,
      permissao: PermissaoEnum.PLANILHA_PRE_FIND_ALL,
      path: ROUTES.preProducaoDetails,
    },
    {
      key: "compras",
      label: "Compras",
      descricao: "Fornecedores e negociação dos itens",
      icon: ShoppingCart,
      permissao: PermissaoEnum.PLANILHA_COMPRAS_FIND_ALL,
      path: ROUTES.compraDetails,
    },
    {
      key: "producao-grafica",
      label: "Produção gráfica",
      descricao: "Acompanhamento da produção dos materiais",
      icon: Printer,
      permissao: PermissaoEnum.PLANILHA_PRODUCAO_GRAFICA_FIND_ALL,
      path: ROUTES.planilhaProducaoGraficaDetails,
    },
  ],
  job: [
    {
      key: "geral",
      label: "Job",
      descricao: "Dados gerais, equipe e documentos do job",
      icon: Briefcase,
      permissao: PermissaoEnum.JOB_FIND_ALL,
      path: ROUTES.jobDetails,
    },
    {
      key: "planejamento",
      label: "Planejamento",
      descricao: "Cronograma e entregas planejadas",
      icon: CalendarRange,
      permissao: PermissaoEnum.JOB_PLANEJAMENTO_FIND_ALL,
      path: ROUTES.jobPlanejamentoDetails,
    },
    {
      key: "tecnologia",
      label: "Tecnologia",
      descricao: "Demandas e execução técnica do job",
      icon: Cpu,
      permissao: PermissaoEnum.JOB_TECNOLOGIA_FIND_ALL,
      path: ROUTES.jobTecnologiaDetails,
    },
    {
      key: "criacao",
      label: "Criação",
      descricao: "Briefing e itens do job, sem valores",
      icon: Palette,
      permissao: PermissaoEnum.ROTA_CRIACAO_JOBS,
      path: ROUTES.criacaoDetails,
    },
  ],
  faturamento: [
    {
      key: "atendimento",
      label: "Atendimento",
      descricao: "Envio ao financeiro e acompanhamento do pedido",
      icon: Headset,
      permissao: PermissaoEnum.FATURAMENTO_ATENDIMENTO_FIND_ALL,
      path: ROUTES.faturamentoAtendimentoDetails,
    },
    {
      key: "financeiro",
      label: "Financeiro",
      descricao: "Nota fiscal, prazos e baixa do faturamento",
      icon: Banknote,
      permissao: PermissaoEnum.FATURAMENTO_FINANCEIRO_FIND_ALL,
      path: ROUTES.faturamentoFinanceiroDetails,
    },
  ],
} satisfies Record<string, readonly ResourceView[]>;

export type ResourceViewKind = keyof typeof RESOURCE_VIEWS;

export type AcessoVisao =
  /** Tem a permissão da visão pedida — segue direto para a página. */
  | { tipo: "permitida" }
  /** Não tem a visão pedida, mas tem exatamente uma outra — abre nela. */
  | { tipo: "redirecionar"; destino: ResourceView }
  /** Não tem a visão pedida e tem mais de uma alternativa — deixa escolher. */
  | { tipo: "escolher"; alternativas: ResourceView[] }
  /** Não tem nenhuma visão do recurso — acesso restrito. */
  | { tipo: "bloqueada" };

export function resolverAcessoVisao(
  views: readonly ResourceView[],
  viewKey: string,
  temPermissao: (permissao: PermissaoEnum) => boolean,
): AcessoVisao {
  const atual = views.find((view) => view.key === viewKey);
  if (!atual || temPermissao(atual.permissao)) return { tipo: "permitida" };

  const alternativas = views.filter((view) => view.key !== viewKey && temPermissao(view.permissao));

  if (alternativas.length === 0) return { tipo: "bloqueada" };
  if (alternativas.length === 1) return { tipo: "redirecionar", destino: alternativas[0] };
  return { tipo: "escolher", alternativas };
}
